import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import Modal from "react-modal";
import { selectUser } from "../auth/auth.selector";
import SIREN_ASSET from "../../assets/sounds/odm-siren.mp3";
import AddLeadForm from "../lead-management/AddLeadForm";
import "../../styles/odm/odm-alert-form.css";

const SOUND_PATH = SIREN_ASSET;
const BC_NAME = "odm-alerts";

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    zIndex: 9999,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    position: "relative",
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    margin: "auto",
    transform: "none",
    width: "90%",
    maxWidth: "480px",
    maxHeight: "85vh",
    padding: "0",
    border: "none",
    borderRadius: "24px",
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    inset: "auto",
  },
};

const withinWorkingHours = (now = new Date()) => {
  try {
    const override = localStorage.getItem("odm_force_hours");
    if (override === "1") return true;
    if (override === "0") return false;
    const h = now.getHours();
    return h >= 6 && h < 22;
  } catch {
    return true;
  }
};

const OdmLeadAlerts = () => {
  const user = useSelector(selectUser);

  const audioRef = useRef(null);
  const audioReadyRef = useRef(false);
  const playRetryTimerRef = useRef(null);
  const timerRef = useRef(null);
  const tabIdRef = useRef(
    Date.now().toString(36) + Math.random().toString(36).substr(2)
  );
  const lastLeadCountRef = useRef(0);
  const lastLeadCountForSirenRef = useRef(0);

  const [muted, setMutedRaw] = useState(() => {
    try {
      return localStorage.getItem("odm_alert_mute") === "1";
    } catch {
      return false;
    }
  });
  const [isMuted] = useState(muted);
  const [isPrimary, setIsPrimary] = useState(true);
  const [needUnlock, setNeedUnlock] = useState(false);
  const [alertLeads, setAlertLeads] = useState([]);
  const [selectedLeadIndex, setSelectedLeadIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showAddLeadForm, setShowAddLeadForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalDismissed, setModalDismissed] = useState(false);

  const ODM_ALERT_WHITELIST = useMemo(
    () => [
      "tinkuchhabra.assetdeals@gmail.com",
      "deepti.chauhan@wtfgyms.com",
      "chetan.sharma@wtfgyms.com",
      "soma@wtfgyms.com",
      "danishansari2088288@gmail.com",
      "tomarshaily012@gmail.com",
      "thakurmayank5345@gmail.com",
      "aditichamyal594@gmail.com",
      "naresh.kukreti@wtfup.me",
    ],
    []
  );

  const isUserWhitelisted = useCallback(
    (user) => {
      if (!user) return false;
      const userEmail = user.email || user.user_email || "";
      if (!userEmail) return false;

      return ODM_ALERT_WHITELIST.includes(userEmail.toLowerCase());
    },
    [ODM_ALERT_WHITELIST]
  );

  const audienceAllows = useCallback(
    (lead, user) => {
      try {
        if (!user || !lead) return false;

        const acc = user.account_type;

        if (acc === "admin") {
          return isUserWhitelisted(user);
        }

        if (acc === "receptionist") {
          return user.gym_id === lead.gym_id;
        }

        if (acc === "cluster_manager") {
          const userGyms = user.gyms?.gyms || user.gyms || [];
          return (
            Array.isArray(userGyms) &&
            userGyms.some((g) => {
              const gymId = g?.value || g?.gym_id || g?.uid || g;
              return gymId === lead.gym_id;
            })
          );
        }

        return false;
      } catch {
        return false;
      }
    },
    [isUserWhitelisted]
  );

  const getSeenSet = useCallback(() => {
    try {
      const userKey = user?.uid || user?.user_id || "default";
      const k = `odm_seen_ids_${userKey}`;
      const stored = localStorage.getItem(k);
      return new Set(stored ? JSON.parse(stored) : []);
    } catch {
      return new Set();
    }
  }, [user]);

  const persistSeen = useCallback(
    (seenSet) => {
      try {
        const userKey = user?.uid || user?.user_id || "default";
        const k = `odm_seen_ids_${userKey}`;
        localStorage.setItem(k, JSON.stringify([...seenSet]));
      } catch {}
    },
    [user]
  );

  const persistMute = useCallback((val) => {
    try {
      localStorage.setItem("odm_alert_mute", val ? "1" : "0");
    } catch {}
  }, []);

  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      try {
        const a = new Audio(SOUND_PATH);
        a.loop = false;
        a.volume = 0.75;
        a.preload = "auto";
        a.muted = false;
        const onReady = () => {
          audioReadyRef.current = true;
        };
        a.addEventListener("canplaythrough", onReady, { once: true });
        a.addEventListener("loadeddata", onReady, { once: true });
        try {
          a.load();
        } catch (e) {}
        audioRef.current = a;
      } catch (e) {}
    }
    return audioRef.current;
  }, []);

  const playSiren = useCallback(async () => {
    if (isMuted) {
      return;
    }
    if (!isPrimary) {
      return;
    }
    if (!showModal) {
      return;
    }
    if (alertLeads.length === 0) {
      return;
    }
    if (modalDismissed) {
      return;
    }

    const currentLead = alertLeads[0];
    if (!currentLead?.id) {
      return;
    }

    try {
      if (
        navigator &&
        navigator.userActivation &&
        navigator.userActivation.hasBeenActive === false
      ) {
        setNeedUnlock(true);
        return;
      }
    } catch {}

    const a = ensureAudio();
    if (!a) {
      return;
    }

    const start = async () => {
      try {
        a.currentTime = 0;
        await a.play();
      } catch (e) {
        setNeedUnlock(true);
      }
    };

    if (audioReadyRef.current) {
      start();
    } else {
      const onceReady = async () => {
        audioReadyRef.current = true;
        start();
      };
      a.addEventListener("canplaythrough", onceReady, { once: true });
      a.addEventListener("loadeddata", onceReady, { once: true });
    }
  }, [ensureAudio, isMuted, isPrimary, showModal, alertLeads, modalDismissed]);

  const stopSiren = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (playRetryTimerRef.current) {
        clearInterval(playRetryTimerRef.current);
        clearTimeout(playRetryTimerRef.current);
        playRetryTimerRef.current = null;
      }
    } catch {}
  }, []);

  const addAlertLead = useCallback(
    (lead) => {
      if (!lead?.id) return;
      setAlertLeads((prev) => {
        const exists = prev.find((l) => l?.id === lead.id);
        if (exists) return prev;
        const newLeads = [...prev, { ...lead, timestamp: Date.now() }];
        return newLeads;
      });

      if (!showModal && !modalDismissed) {
        setShowModal(true);
        setSelectedLeadIndex(0);
      }
    },
    [showModal, modalDismissed]
  );

  const getLeadTimeRemaining = useCallback((leadTimestamp) => {
    const now = Date.now();
    const elapsed = Math.floor((now - leadTimestamp) / 1000);
    const remaining = Math.max(0, 300 - elapsed);
    return remaining;
  }, []);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const removeExpiredLeads = useCallback(() => {
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;

    setAlertLeads((prev) => {
      const filtered = prev.filter((lead) => lead.timestamp > fiveMinutesAgo);
      if (filtered.length === 0 && prev.length > 0) {
        setShowModal(false);
        setSelectedLeadIndex(0);
        stopSiren();
      } else if (selectedLeadIndex >= filtered.length) {
        setSelectedLeadIndex(Math.max(0, filtered.length - 1));
      }
      return filtered;
    });
  }, [selectedLeadIndex, stopSiren]);

  const removeAlertLead = useCallback(
    (leadId) => {
      setAlertLeads((prev) => {
        const filtered = prev.filter((l) => l?.id !== leadId);
        if (filtered.length === 0) {
          setShowModal(false);
          setSelectedLeadIndex(0);
          stopSiren();
        } else if (selectedLeadIndex >= filtered.length) {
          setSelectedLeadIndex(Math.max(0, filtered.length - 1));
        }
        return filtered;
      });
    },
    [selectedLeadIndex, stopSiren]
  );

  const handleAssignToMe = useCallback(() => {
    const currentLead = alertLeads?.[selectedLeadIndex];
    if (!currentLead?.id) return;
    setSelectedLead(currentLead);
    setShowAddLeadForm(true);
  }, [alertLeads, selectedLeadIndex]);

  const closeAddLeadForm = useCallback(() => {
    setShowAddLeadForm(false);
    setSelectedLead(null);
  }, []);

  const onLeadFormSubmit = useCallback(() => {
    if (selectedLead?.id) {
      removeAlertLead(selectedLead.id);
    }
    closeAddLeadForm();
  }, [selectedLead, removeAlertLead, closeAddLeadForm]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setModalDismissed(true);
  }, []);

  useEffect(() => {
    if (alertLeads.length > lastLeadCountRef.current && modalDismissed) {
      setModalDismissed(false);
      setShowModal(true);
    }
    lastLeadCountRef.current = alertLeads.length;
  }, [alertLeads.length, modalDismissed]);

  useEffect(() => {
    if (
      alertLeads.length > 0 &&
      !showModal &&
      !showAddLeadForm &&
      !modalDismissed
    ) {
      setShowModal(true);
    }
  }, [alertLeads.length, showModal, showAddLeadForm, modalDismissed]);

  useEffect(() => {
    const unlock = async () => {
      try {
        if (!needUnlock || alertLeads.length > 0) return;

        const a = ensureAudio();
        if (!a) return;

        const originalVolume = a.volume;
        a.volume = 0;
        await a.play();
        a.pause();
        a.currentTime = 0;
        a.volume = originalVolume;
        setNeedUnlock(false);
      } catch {}
    };

    if (needUnlock) {
      window.addEventListener("pointerdown", unlock, true);
      return () => window.removeEventListener("pointerdown", unlock, true);
    }
  }, [ensureAudio, needUnlock, alertLeads.length]);

  const manualUnlock = useCallback(async () => {
    try {
      const a = ensureAudio();
      if (!a) return;

      const originalVolume = a.volume;
      a.volume = 0.1;
      await a.play();
      a.pause();
      a.currentTime = 0;
      a.volume = originalVolume;
      setNeedUnlock(false);
    } catch (e) {
      console.warn("[ODM] manual unlock failed", e);
    }
  }, [ensureAudio]);

  useEffect(() => {
    const hasValidLeads = alertLeads.length > 0 && alertLeads[0]?.id;
    const shouldTrigger =
      showModal && hasValidLeads && isPrimary && !modalDismissed;

    if (shouldTrigger) {
      const isNewModal =
        lastLeadCountForSirenRef.current === 0 && alertLeads.length > 0;
      const hasNewLeads = alertLeads.length > lastLeadCountForSirenRef.current;

      if (isNewModal || hasNewLeads) {
        setTimeout(() => playSiren(), 100);
        lastLeadCountForSirenRef.current = alertLeads.length;
      }
    } else {
      lastLeadCountForSirenRef.current = 0;
      stopSiren();
    }
  }, [showModal, alertLeads, isPrimary, modalDismissed, playSiren, stopSiren]);

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel(BC_NAME);
    } catch {
      setIsPrimary(true);
      return;
    }

    const currentTabId = tabIdRef.current;
    let heartbeat;
    const sendHeartbeat = () => {
      try {
        bc.postMessage({ type: "HEARTBEAT", tabId: currentTabId });
      } catch {}
    };
    const elect = () => {
      const now = Date.now();
      const tabs = JSON.parse(localStorage.getItem("odm_tabs") || "{}");
      tabs[currentTabId] = now;

      Object.keys(tabs).forEach((tid) => {
        if (now - tabs[tid] > 8000) delete tabs[tid];
      });
      localStorage.setItem("odm_tabs", JSON.stringify(tabs));

      const sorted = Object.entries(tabs).sort(([, a], [, b]) => a - b);
      const primary = sorted[0]?.[0] === currentTabId;
      setIsPrimary(primary);
    };

    bc.addEventListener("message", (ev) => {
      if (ev.data?.type === "HEARTBEAT") elect();
    });

    elect();
    heartbeat = setInterval(() => {
      sendHeartbeat();
      elect();
    }, 3000);

    return () => {
      clearInterval(heartbeat);
      try {
        bc.close();
      } catch {}
      try {
        const tabs = JSON.parse(localStorage.getItem("odm_tabs") || "{}");
        delete tabs[currentTabId];
        localStorage.setItem("odm_tabs", JSON.stringify(tabs));
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (alertLeads.length > 0) {
      timerRef.current = setInterval(() => {
        removeExpiredLeads();
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [alertLeads.length, removeExpiredLeads]);

  useEffect(() => {
    if (isMuted !== muted) {
      setMutedRaw(isMuted);
      persistMute(isMuted);
    }
  }, [isMuted, muted, persistMute]);

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel("odm-alerts-events");
    } catch {
      return;
    }

    const handler = (ev) => {
      try {
        const msg = ev?.data || {};
        if (msg?.event_type !== "new_odm_added" || !msg?.status || !msg?.data) {
          return;
        }

        const data = msg.data;
        const lead = {
          id: data?.odm_id || data?.id || Date.now().toString(),
          name: data?.user_name || "Unknown",
          mobile: data?.user_mobile || "",
          gym_id: data?.gym_id || "",
          gym_name: data?.gym_name || "",
          preferred_slot: data?.preferred_slot || "",
          lead_from: data?.lead_from || "",
          email: data?.user_email || "",
        };

        const within = withinWorkingHours();
        if (!within) return;

        const allowed = audienceAllows(lead, user);
        if (!allowed) return;

        const seen = getSeenSet();
        const dedupeDisabled =
          localStorage.getItem("odm_disable_dedupe") === "1";
        const isSeen = lead.id != null && seen.has(String(lead.id));
        if (isSeen && !dedupeDisabled) return;

        if (lead.id != null) {
          seen.add(String(lead.id));
          persistSeen(seen);
        }

        if (isPrimary) {
          addAlertLead(lead);
        }
      } catch (err) {
        console.error("[ODM Alerts] Error processing event:", err);
      }
    };

    bc.addEventListener("message", handler);
    return () => {
      try {
        bc.removeEventListener("message", handler);
        bc.close();
      } catch {}
    };
  }, [user, isPrimary, getSeenSet, persistSeen, addAlertLead, audienceAllows]);

  useEffect(() => {
    return () => {
      stopSiren();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [stopSiren]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__ODM_ALERTS_DEBUG__ = {
        trigger: (debugLead = {}) => {
          if (isPrimary) {
            const lead = {
              id: Date.now().toString(),
              name: debugLead.name || "",
              mobile: debugLead.mobile || "",
              gym_id: debugLead.gym_id || user?.gym_id || "",
              gym_name: debugLead.gym_name || "",
              ...debugLead,
            };
            addAlertLead(lead);
          }
        },
        clearAll: () => {
          setAlertLeads([]);
          setShowModal(false);
          setModalDismissed(false);
          lastLeadCountForSirenRef.current = 0;
          stopSiren();
        },
        showModal: () => setShowModal(true),
        hideModal: () => setShowModal(false),
        testAudio: async () => {
          try {
            const a = ensureAudio();

            await a.play();
          } catch (e) {}
        },
        forcePlaySiren: () => {
          playSiren();
        },
        emergencyBeep: () => {
          try {
            const ctx = new (window.AudioContext ||
              window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 800;
            osc.type = "sine";
            gain.gain.value = 0.3;
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
          } catch (e) {}
        },
      };
    }
  }, [isPrimary, addAlertLead, user, stopSiren, ensureAudio, playSiren]);

  const currentLead = alertLeads?.[selectedLeadIndex];

  if (user?.account_type === "admin" && !isUserWhitelisted(user)) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={showModal && !showAddLeadForm}
        onRequestClose={handleModalClose}
        style={modalStyles}
        contentLabel="ODM Alert"
        ariaHideApp={false}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
      >
        <div className="odm-alert-container">
          <div className="odm-alert-header">
            {currentLead && (
              <div className="odm-alert-timer">
                {formatTime(getLeadTimeRemaining(currentLead.timestamp))}
              </div>
            )}
            <h2 className="odm-alert-title">ODM: New Lead Alert</h2>
            <button
              className="odm-alert-close"
              onClick={handleModalClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="odm-alert-body">
            {alertLeads?.length > 1 && (
              <div
                className={`odm-alert-lead-tabs ${
                  alertLeads.length > 5 ? "odm-alert-has-many-leads" : ""
                }`}
              >
                <div className="odm-alert-tabs-container">
                  {alertLeads.map((lead, index) => (
                    <button
                      key={lead?.id || index}
                      onClick={() => setSelectedLeadIndex(index)}
                      className={`odm-alert-tab-button ${
                        selectedLeadIndex === index ? "odm-alert-active" : ""
                      }`}
                    >
                      Lead {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentLead && (
              <div className="odm-alert-lead-details">
                <h3 className="odm-alert-lead-name">
                  {currentLead.name || "New Lead"}
                </h3>
                <div className="odm-alert-lead-info">
                  <div className="odm-alert-info-row">
                    <span className="odm-alert-info-label" data-type="mobile">
                      Mobile
                    </span>
                    <span className="odm-alert-info-value">
                      {currentLead.mobile}
                    </span>
                  </div>
                  <div className="odm-alert-info-row">
                    <span className="odm-alert-info-label" data-type="gym">
                      Gym
                    </span>
                    <span className="odm-alert-info-value">
                      {currentLead.gym_name || currentLead.gym_id || "N/A"}
                    </span>
                  </div>
                  {currentLead.preferred_slot && (
                    <div className="odm-alert-info-row">
                      <span className="odm-alert-info-label" data-type="slot">
                        Preferred Slot
                      </span>
                      <span className="odm-alert-info-value">
                        {currentLead.preferred_slot}
                      </span>
                    </div>
                  )}
                  {currentLead.lead_from && (
                    <div className="odm-alert-info-row">
                      <span className="odm-alert-info-label" data-type="source">
                        Source
                      </span>
                      <span className="odm-alert-info-value">
                        {currentLead.lead_from}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="odm-alert-action-buttons">
              {/* <button
                onClick={handleStartCall}
                className="odm-alert-btn odm-alert-btn-call"
              >
                Start Call
              </button> */}
              <button
                onClick={handleAssignToMe}
                className="odm-alert-btn odm-alert-btn-assign"
              >
                Assign to Me
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showAddLeadForm}
        onRequestClose={closeAddLeadForm}
        contentLabel="Assign Lead"
        ariaHideApp={false}
      >
        <div className="createNewCTOModalContainer p-0 mod-width-50p mod-height-95vh">
          {selectedLead && (
            <AddLeadForm
              userInfoData={user}
              closeModal={closeAddLeadForm}
              callFunction={onLeadFormSubmit}
              rowData={{
                name: selectedLead.name,
                user_name: selectedLead.name,
                mobile: selectedLead.mobile,
                user_mobile: selectedLead.mobile,
                email: selectedLead.email,
                user_email: selectedLead.email,
                gym_id: selectedLead.gym_id,
                gym_name: selectedLead.gym_name,
                preferred_slot: selectedLead.preferred_slot,
                lead_from: selectedLead.lead_from,
              }}
              isODM={true}
              odmSource="one_day_membership"
            />
          )}
        </div>
      </Modal>

      <div className="odm-alert-control-buttons">
        {needUnlock && (
          <button
            onClick={manualUnlock}
            className="odm-alert-control-btn odm-alert-unlock-btn"
          >
            Enable Sound
          </button>
        )}
        {/* {showModal && (
          <button
            onClick={() => setIsMuted((v) => !v)}
            className={`odm-alert-control-btn odm-alert-mute-btn ${isMuted ? "odm-alert-muted" : ""}`}
          >
            {isMuted ? "Unmute" : "Mute"}
          </button>
        )} */}
      </div>
    </>
  );
};

export default OdmLeadAlerts;
